import React from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from "../lib/convex";
import { Pencil, Trash2, Save, X, Plus } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

export interface WritingStylesManagerProps {
  isOpen: boolean;
  onToggle?: () => void;
}

type EditingStyle = {
  id: string;
  name: string;
  description: string;
  systemPrompt: string;
};

export default function WritingStylesManager({ isOpen }: WritingStylesManagerProps) {
  const writingStyles = useQuery(api.posts.getWritingStyles);
  const updateStyle = useMutation(api.posts.updateWritingStyle);
  const createStyle = useMutation(api.posts.createWritingStyle);
  const deleteStyle = useMutation(api.posts.deleteWritingStyle);
  const [editingStyle, setEditingStyle] = React.useState<EditingStyle | null>(null);
  const [newStyle, setNewStyle] = React.useState({
    name: '',
    description: '',
    systemPrompt: '',
  });
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  const handleEdit = (style: any) => {
    setEditingStyle({
      id: style._id,
      name: style.name,
      description: style.description,
      systemPrompt: style.systemPrompt,
    });
  };

  const handleSave = async () => {
    if (!editingStyle) return;
    
    try {
      await updateStyle({
        id: editingStyle.id as any,
        name: editingStyle.name,
        description: editingStyle.description,
        systemPrompt: editingStyle.systemPrompt,
      });
      setEditingStyle(null);
    } catch (error) {
      console.error('Error updating writing style:', error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteStyle({ id: id as any });
    } catch (error) {
      console.error('Error deleting writing style:', error);
    }
  };

  const handleCreateStyle = async () => {
    try {
      await createStyle({
        name: newStyle.name,
        description: newStyle.description,
        systemPrompt: newStyle.systemPrompt,
        isCustom: true,
      });
      setNewStyle({ name: '', description: '', systemPrompt: '' });
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Error creating writing style:', error);
    }
  };

  return (
    <div className={`fixed top-16 right-0 h-[calc(100vh-8rem)] overflow-y-auto transition-all duration-300 z-40 ${
      isOpen ? 'w-96' : 'w-0'
    }`}>

      {isOpen && (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Writing Styles</h2>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                New Style
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Writing Style</DialogTitle>
                <DialogDescription>
                  Create a custom writing style for your blog posts.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={newStyle.name}
                    onChange={(e) => setNewStyle({ ...newStyle, name: e.target.value })}
                    placeholder="e.g., Technical Expert"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input
                    value={newStyle.description}
                    onChange={(e) => setNewStyle({ ...newStyle, description: e.target.value })}
                    placeholder="e.g., Clear and detailed technical explanations"
                  />
                </div>
                <div>
                  <Label>Writing Instructions</Label>
                  <div className="relative">
                    <textarea
                      value={newStyle.systemPrompt}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setNewStyle(prev => ({
                          ...prev,
                          systemPrompt: newValue.slice(0, 1000)
                        }));
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pastedText = e.clipboardData.getData('text');
                        const currentText = newStyle.systemPrompt;
                        const newText = currentText + pastedText;
                        setNewStyle(prev => ({
                          ...prev,
                          systemPrompt: newText.slice(0, 1000)
                        }));
                      }}
                      className="w-full min-h-[100px] p-2 border rounded-md pr-16"
                      placeholder="Describe how the content should be written..."
                    />
                    <div className="absolute bottom-2 right-2 text-xs text-muted-foreground">
                      {newStyle.systemPrompt.length}/1000
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={handleCreateStyle}
                  disabled={!newStyle.name || !newStyle.description || !newStyle.systemPrompt}
                >
                  Create Style
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <div className="space-y-6">
          {writingStyles?.filter(style => style.isCustom).map((style) => (
            <div key={style._id} className="bg-card rounded-lg p-4 shadow-sm border">
              {editingStyle?.id === style._id ? (
                <div className="space-y-4">
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={editingStyle.name}
                      onChange={(e) => setEditingStyle({ ...editingStyle, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input
                      value={editingStyle.description}
                      onChange={(e) => setEditingStyle({ ...editingStyle, description: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Writing Instructions</Label>
                    <textarea
                      value={editingStyle.systemPrompt}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setEditingStyle(prev => ({
                          ...prev,
                          systemPrompt: newValue.slice(0, 1000)
                        }));
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pastedText = e.clipboardData.getData('text');
                        const currentText = editingStyle.systemPrompt;
                        const newText = currentText + pastedText;
                        setEditingStyle(prev => ({
                          ...prev,
                          systemPrompt: newText.slice(0, 1000)
                        }));
                      }}
                      className="w-full min-h-[100px] p-2 border rounded-md pr-16"
                      maxLength={1000}
                    />
                    <div className="text-xs text-muted-foreground text-right mt-1">
                      {editingStyle.systemPrompt.length}/1000
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleSave} size="sm">
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingStyle(null)}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold">{style.name}</h3>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(style)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(style._id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{style.description}</p>
                  <pre className="text-sm bg-muted p-2 rounded-md whitespace-pre-wrap">
                    {style.systemPrompt}
                  </pre>
                </div>
              )}
            </div>
          ))}
          {writingStyles?.filter(style => style.isCustom).length === 0 && (
            <p className="text-center text-muted-foreground">
              No custom writing styles yet. Create one by selecting "Custom Style" when writing a blog post.
            </p>
          )}
        </div>
      </div>
      )}
    </div>
  );
}